import React from 'react';
import _merge from 'lodash/merge';

import { getContext, isAnimationDisabled } from 'common';
import Styled, { FadeOut } from './styles';

export interface CircularProgressIndicatorIndeterminateProps extends React.ComponentPropsWithRef<'div'> {
  /** strokeWidth is the width of the stroke to be applied to the circle. */
  strokeWidth?: number;
  /** If set to 'true' the progress indicator will fade out over 0.3 seconds */
  fade?: boolean;
  /** Control animation config here. This uses react-spring transitions config. (useSpring hook) See https://react-spring.io for more information. */
  animationProps?: any;
  /** If true, slow down all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** If disableAnimation = true, this is the animation duration of the indicator (sec). */
  animationDuration?: number;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
}

/** CircularProgressIndicatorIndeterminate express an unspecified amount of wait time - continually
growing and shrinking in size until the process is complete. ref is forwarded to the
Wrapper component. */
const CircularProgressIndicatorIndeterminate = React.forwardRef<HTMLElement, CircularProgressIndicatorIndeterminateProps>((props: CircularProgressIndicatorIndeterminateProps, ref) => getContext(({ RTL, theme, disableAnimation }) => {
  const {
    strokeWidth,
    fade,
    animationProps,
    disableAnimation: propsdisableAnimation,
    animationDuration,
    theme: themeProp,
    ...otherProps
  } = props;
  const resolvedTheme = _merge({}, theme ? theme.CIRCULAR_PROGRESS_INDETERMINATE : {}, themeProp);
  const resolvedDisabledAnimation = isAnimationDisabled(propsdisableAnimation, disableAnimation);
  return (
    <FadeOut fade={fade} {...animationProps} disableAnimation={resolvedDisabledAnimation}>
      <Styled.Wrapper ref={ref} {...otherProps}>
        <Styled.SvgContainer $disableAnimation={resolvedDisabledAnimation} $animationDuration={animationDuration} rtl={RTL} viewBox="0 0 48 48">
          <Styled.CircleContainer
            rtl={RTL}
            $disableAnimation={resolvedDisabledAnimation}
            $animationDuration={animationDuration}
            $theme={resolvedTheme}
            strokeWidth={strokeWidth}
            cx={24}
            cy={24}
            r={18.5}
          />
        </Styled.SvgContainer>
      </Styled.Wrapper>
    </FadeOut>
  );
}));

CircularProgressIndicatorIndeterminate.displayName = 'CircularProgressIndicatorIndeterminate';

CircularProgressIndicatorIndeterminate.defaultProps = {
  strokeWidth: 4,
  fade: false,
  animationProps: {},
  disableAnimation: undefined,
  animationDuration: 30,
  theme: {},
};

export default CircularProgressIndicatorIndeterminate;
